import { useState } from "react";
import type {
  Category,
  CategoryFilter,
  EventId,
  PlanEvent,
} from "../../../shared/types";
import { useScheduleTemplate } from "../hooks/use-schedule-template";
import { useScheduleImportExport } from "../hooks/use-schedule-import-export";
import type { TemplateSlot } from "../utils/derive-template";
import { ScheduleHeader } from "./ScheduleHeader";
import { ScheduleDailySection } from "./ScheduleDailySection";
import { ScheduleWeeklyDesktop } from "./ScheduleWeeklyDesktop";
import { ScheduleWeeklyMobile } from "./ScheduleWeeklyMobile";
import { ScheduleMonthlySection } from "./ScheduleMonthlySection";
import { SchedulePeriodicSection } from "./SchedulePeriodicSection";
import { ScheduleModals } from "./ScheduleModals";

interface ScheduleViewProps {
  readonly events: PlanEvent[];
  readonly categoryFilter: CategoryFilter;
  readonly defaultCategory: Category;
  readonly onUpdateEvent: (
    id: EventId,
    updates: Partial<
      Pick<
        PlanEvent,
        "title" | "startTime" | "endTime" | "category" | "description" | "tags"
      >
    >,
  ) => void;
  readonly onAddEventFull: (data: Omit<PlanEvent, "id" | "createdAt">) => void;
  readonly onDeleteEvent: (id: EventId) => void;
}

export function ScheduleView({
  events,
  categoryFilter,
  defaultCategory,
  onUpdateEvent,
  onAddEventFull,
  onDeleteEvent,
}: ScheduleViewProps) {
  const template = useScheduleTemplate(events, categoryFilter);
  const { handleExport, handleImport } = useScheduleImportExport({
    events,
    onAddEventFull,
  });

  const [editingSlot, setEditingSlot] = useState<TemplateSlot | null>(null);
  const [addingForDay, setAddingForDay] = useState<number | null>(null);
  const [addingRecurrenceType, setAddingRecurrenceType] = useState<"daily" | "weekly" | "monthly" | "quarterly" | "semiannual" | "yearly">("weekly");
  const [ctxMenu, setCtxMenu] = useState<{ slot: TemplateSlot; anchorRect: DOMRect } | null>(null);
  const [deletingSlot, setDeletingSlot] = useState<TemplateSlot | null>(null);

  const handleEditSlot = (slot: TemplateSlot | null) => {
    setAddingForDay(null);
    setEditingSlot(slot);
  };

  const handleAddSlot = (day: number | null, type: "daily" | "weekly" | "monthly" | "quarterly" | "semiannual" | "yearly" = "weekly") => {
    setEditingSlot(null);
    setAddingRecurrenceType(type);
    setAddingForDay(day);
  };

  const handleContextMenu = (slot: TemplateSlot, anchorRect: DOMRect) => {
    setCtxMenu({ slot, anchorRect });
  };

  return (
    <div className="flex flex-col gap-5 pt-6 pb-24 w-full">
      <ScheduleHeader onExport={handleExport} onImport={handleImport} />

      <ScheduleDailySection
        slots={template.daily}
        onEditSlot={handleEditSlot}
        onAddSlot={() => handleAddSlot(0, "daily")}
        onContextMenu={handleContextMenu}
      />

      <ScheduleWeeklyDesktop
        weeklySlots={template.weekly}
        onEditSlot={handleEditSlot}
        onAddSlot={handleAddSlot}
        onContextMenu={handleContextMenu}
      />

      <ScheduleWeeklyMobile
        weeklySlots={template.weekly}
        onEditSlot={handleEditSlot}
        onAddSlot={handleAddSlot}
        onContextMenu={handleContextMenu}
      />

      <ScheduleMonthlySection
        slots={template.monthly}
        onEditSlot={handleEditSlot}
        onAddSlot={() => handleAddSlot(0, "monthly")}
        onContextMenu={handleContextMenu}
      />

      <SchedulePeriodicSection
        title="Trimestriel"
        slots={template.quarterly}
        onEditSlot={handleEditSlot}
        onAddSlot={() => handleAddSlot(0, "quarterly")}
        onContextMenu={handleContextMenu}
      />

      <SchedulePeriodicSection
        title="Semestriel"
        slots={template.semiannual}
        onEditSlot={handleEditSlot}
        onAddSlot={() => handleAddSlot(0, "semiannual")}
        onContextMenu={handleContextMenu}
      />

      <SchedulePeriodicSection
        title="Annuel"
        slots={template.yearly}
        onEditSlot={handleEditSlot}
        onAddSlot={() => handleAddSlot(0, "yearly")}
        onContextMenu={handleContextMenu}
      />

      <ScheduleModals
        editingSlot={editingSlot}
        addingForDay={addingForDay}
        addingRecurrenceType={addingRecurrenceType}
        ctxMenu={ctxMenu}
        deletingSlot={deletingSlot}
        defaultCategory={defaultCategory}
        events={events}
        onEditSlot={handleEditSlot}
        onAddSlot={handleAddSlot}
        onContextMenu={setCtxMenu}
        onDeleteSlot={setDeletingSlot}
        onUpdateEvent={onUpdateEvent}
        onAddEventFull={onAddEventFull}
        onDeleteEvent={onDeleteEvent}
      />
    </div>
  );
}
